import React, { useEffect, useState } from 'react'
import {
  IconButton,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  useToggle
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import { useCompanyLazyQuery } from '@paintscout/api'
import type { Company } from '@paintscout/api'
import { useCompanyMutations } from 'shared/hooks'
import CompanyForm from 'src/components/forms/CompanyForm'
import ClearIcon from '@material-ui/icons/Clear'
import { track } from 'shared/util/analytics'
import { useConfirmBeforeCancel } from '../useConfirmBeforeCancel'

const useStyles = makeStyles<Theme, CompanyDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogPaper: {
      overflow: 'visible',
      '@media (max-height: 700px)': {
        overflow: 'auto'
      },
      [theme.breakpoints.down('xs')]: {
        overflow: 'auto'
      }
    },
    dialogContent: {
      overflow: 'visible',
      '@media (max-height: 700px)': {
        overflow: 'auto'
      },
      [theme.breakpoints.down('xs')]: {
        overflow: 'auto'
      }
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(1.5)
    },
    cancel: {},
    confirm: {
      padding: theme.spacing(1.5, 3)
    }
  }),
  { name: 'CompanyDialog' }
)

export interface CompanyDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  companyId?: string
  confirmBeforeCancelWhenDirty?: boolean
  onConfirm: (company: Company) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  onBack?: (event: React.MouseEvent<HTMLElement>) => any
}

function CompanyDialog(props: CompanyDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, companyId, onBack, confirmBeforeCancelWhenDirty = false, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const { saveCompany } = useCompanyMutations()

  const { value: isSaving, setValue: setSaving } = useToggle(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [fetchCompany, { data, loading: fetching, error }] = useCompanyLazyQuery({
    fetchPolicy: 'cache-and-network'
  })

  const [company, setCompany] = useState(data?.company)
  const loading = (fetching && !company && !!companyId) || isSaving
  const isCreating = !companyId

  const handleRequestCancel = useConfirmBeforeCancel({
    confirmBeforeCancelWhenDirty,
    isFormDirty,
    onCancel
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, { tags: { companyId } })
      enqueueSnackbar('Unable to load Contact', { variant: 'error' })
      onCancel(null)
    }
  }, [error])

  useEffect(() => {
    if (companyId) {
      fetchCompany({ variables: { id: companyId } })
    }
  }, [companyId])

  useEffect(() => {
    if (data?.company) {
      setCompany(data.company)
    }
  }, [data?.company])

  return (
    <Dialog
      {...baseDialogProps}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      onClose={() => handleRequestCancel()}
    >
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={handleRequestCancel}>
            <ClearIcon />
          </IconButton>
        }
      >
        <Typography variant="h2">{isCreating ? 'Create New' : 'Edit'} Company</Typography>
      </DialogTitle>
      <DialogContent dividers classes={{ root: classes.dialogContent }}>
        <CompanyForm
          id="company-dialog-form"
          data-testid="company-dialog-form"
          onSubmit={handleSubmit}
          onDirtyChange={setIsFormDirty}
          disabled={loading}
          company={company}
        />
      </DialogContent>
      <DialogActions
        leftButton={
          onBack ? (
            <Button variant="textNoPadding" onClick={onBack}>
              Back
            </Button>
          ) : null
        }
      >
        <Button
          className={classes.confirm}
          type="submit"
          form="company-dialog-form"
          variant={'contained'}
          disabled={isLoading}
          data-testid="company-dialog-confirm-button"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(newCompany: Company) {
    track(newCompany._id ? 'company_updated' : 'company_created', { companyId: newCompany._id })
    setIsLoading(true)
    setSaving(true)
    try {
      const saveResult = await saveCompany({ company: newCompany })
      console.log({ saveResult })
      onConfirm(saveResult.company)
    } catch (error) {
      setCompany(newCompany)
      const errorMessage = error?.graphQLErrors?.[0]?.message
      Sentry.captureException(error, { tags: { companyId } })
      enqueueSnackbar(`Unable to save Company${errorMessage ? ` - ${errorMessage}` : ''}`, { variant: 'error' })
    }
    setIsLoading(false)
    setSaving(false)
  }
}

export default CompanyDialog
